/**
 * Utilitaire de Device Fingerprinting
 * Génère une empreinte unique pour identifier un appareil
 */

interface DeviceInfo {
  userAgent: string;
  language: string;
  platform: string;
  screenResolution: string;
  colorDepth: number;
  timezone: string;
  hardwareConcurrency: number;
  deviceMemory?: number;
  maxTouchPoints: number;
}

/**
 * Collecte les informations de l'appareil
 */
export function getDeviceInfo(): DeviceInfo {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenResolution: `${screen.width}x${screen.height}`,
    colorDepth: screen.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
    deviceMemory: (navigator as any).deviceMemory,
    maxTouchPoints: navigator.maxTouchPoints || 0,
  };
}

/**
 * Génère un hash simple à partir d'une chaîne
 */
async function simpleHash(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Génère une empreinte unique de l'appareil
 */
export async function generateDeviceFingerprint(): Promise<string> {
  const info = getDeviceInfo();

  // Créer une chaîne unique à partir des informations de l'appareil
  const fingerprintString = [
    info.userAgent,
    info.platform,
    info.screenResolution,
    info.colorDepth,
    info.timezone,
    info.hardwareConcurrency,
    info.deviceMemory || 'unknown',
    info.maxTouchPoints,
  ].join('|');

  // Générer un hash
  return await simpleHash(fingerprintString);
}

/**
 * Génère un nom descriptif pour l'appareil
 */
export function generateDeviceName(): string {
  const info = getDeviceInfo();
  const ua = info.userAgent.toLowerCase();

  // Détecter le type d'appareil
  let deviceType = 'PC';
  if (/mobile|android|iphone|ipad|tablet/.test(ua)) {
    deviceType = 'Mobile';
  } else if (/tablet|ipad/.test(ua)) {
    deviceType = 'Tablette';
  }

  // Détecter le navigateur
  let browser = 'Navigateur';
  if (ua.includes('chrome') && !ua.includes('edg')) {
    browser = 'Chrome';
  } else if (ua.includes('firefox')) {
    browser = 'Firefox';
  } else if (ua.includes('safari') && !ua.includes('chrome')) {
    browser = 'Safari';
  } else if (ua.includes('edg')) {
    browser = 'Edge';
  }

  // Détecter l'OS
  let os = '';
  if (ua.includes('windows')) {
    os = 'Windows';
  } else if (ua.includes('mac')) {
    os = 'Mac';
  } else if (ua.includes('linux')) {
    os = 'Linux';
  } else if (ua.includes('android')) {
    os = 'Android';
  } else if (ua.includes('ios') || ua.includes('iphone') || ua.includes('ipad')) {
    os = 'iOS';
  }

  // Générer le nom
  const parts = [deviceType];
  if (os) parts.push(os);
  if (browser) parts.push(browser);

  return parts.join(' - ');
}

/**
 * Stocke l'empreinte de l'appareil dans le localStorage
 */
export function storeDeviceFingerprint(fingerprint: string): void {
  localStorage.setItem('device_fingerprint', fingerprint);
}

/**
 * Récupère l'empreinte de l'appareil depuis le localStorage
 */
export function getStoredDeviceFingerprint(): string | null {
  return localStorage.getItem('device_fingerprint');
}

/**
 * Obtient ou génère l'empreinte de l'appareil
 */
export async function getOrCreateDeviceFingerprint(): Promise<string> {
  // Vérifier si on a déjà une empreinte stockée
  let fingerprint = getStoredDeviceFingerprint();

  if (!fingerprint) {
    // Générer une nouvelle empreinte
    fingerprint = await generateDeviceFingerprint();
    storeDeviceFingerprint(fingerprint);
  }

  return fingerprint;
}
